import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import logo from "@/assets/logo.png";
import { useTeams } from "@/hooks/useTeams";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [mobileTeamsOpen, setMobileTeamsOpen] = useState(false);
  const teams = useTeams();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = () => {
    setOpen(false);
    setMobileTeamsOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#050505]/90 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src={logo}
            alt="Proxima League"
            className="w-9 h-9 object-contain drop-shadow-[0_0_8px_rgba(234,179,8,0.35)]"
          />
          <span className="font-display text-xl tracking-wider">
            PROXIMA<span className="text-[#EAB308]">·</span>LEAGUE
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          <a
            href="/#torneo"
            className="text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
            data-testid="nav-link-torneo"
          >
            TORNEO
          </a>
          <a
            href="/#formato"
            className="text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
            data-testid="nav-link-formato"
          >
            FORMATO
          </a>

          {/* Equipos dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setTeamsOpen(true)}
            onMouseLeave={() => setTeamsOpen(false)}
          >
            <a
              href="/#equipos"
              className="flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors py-2"
              data-testid="nav-link-equipos"
            >
              EQUIPOS
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  teamsOpen ? "rotate-180 text-[#EAB308]" : ""
                }`}
                strokeWidth={1.5}
              />
            </a>
            {teamsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-60">
                <div
                  className="bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 py-2"
                  data-testid="nav-teams-dropdown"
                >
                  <a
                    href="/#equipos"
                    className="block px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                    data-testid="nav-teams-all"
                  >
                    Todos los equipos
                  </a>
                  {teams.length > 0 && <div className="h-px bg-white/10 my-1 mx-3" />}
                  {teams.map((t) => (
                    <Link
                      key={t.slug}
                      to={`/equipos/${t.slug}`}
                      onClick={() => setTeamsOpen(false)}
                      className="block px-5 py-2.5 text-xs tracking-[0.15em] uppercase text-white/70 hover:text-[#EAB308] hover:bg-white/5 transition-colors"
                      data-testid={`nav-team-link-${t.slug}`}
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="/#inscripciones"
            className="text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
            data-testid="nav-link-inscripciones"
          >
            INSCRIPCIONES
          </a>
        </nav>

        <a
          href="/#inscripciones"
          data-testid="nav-inscribir-btn"
          className="hidden md:inline-flex items-center gap-2 bg-[#EAB308] text-black px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold hover:bg-[#facc15] transition-all hover:-translate-y-0.5"
        >
          Inscribir Equipo
          <span className="text-base leading-none">→</span>
        </a>

        <button
          aria-label="Menu"
          onClick={() => setOpen(!open)}
          className="md:hidden text-white"
          data-testid="nav-mobile-toggle"
        >
          <div className="space-y-1.5">
            <span className={`block w-6 h-px bg-white transition-transform ${open ? "translate-y-2 rotate-45" : ""}`} />
            <span className={`block w-6 h-px bg-white transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-px bg-white transition-transform ${open ? "-translate-y-2 -rotate-45" : ""}`} />
          </div>
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            <a href="/#torneo" onClick={closeMobile} className="text-sm tracking-[0.2em] uppercase text-white/80">
              TORNEO
            </a>
            <a href="/#formato" onClick={closeMobile} className="text-sm tracking-[0.2em] uppercase text-white/80">
              FORMATO
            </a>

            <div>
              <button
                onClick={() => setMobileTeamsOpen(!mobileTeamsOpen)}
                className="flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-white/80"
                data-testid="nav-mobile-teams-toggle"
              >
                EQUIPOS
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${mobileTeamsOpen ? "rotate-180 text-[#EAB308]" : ""}`}
                  strokeWidth={1.5}
                />
              </button>
              {mobileTeamsOpen && (
                <div className="mt-3 ml-4 border-l border-white/10 pl-4 flex flex-col gap-3">
                  <a
                    href="/#equipos"
                    onClick={closeMobile}
                    className="text-xs tracking-[0.2em] uppercase text-white/50"
                  >
                    Todos los equipos
                  </a>
                  {teams.map((t) => (
                    <Link
                      key={t.slug}
                      to={`/equipos/${t.slug}`}
                      onClick={closeMobile}
                      className="text-xs tracking-[0.2em] uppercase text-white/70"
                      data-testid={`nav-mobile-team-link-${t.slug}`}
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a href="/#inscripciones" onClick={closeMobile} className="text-sm tracking-[0.2em] uppercase text-white/80">
              INSCRIPCIONES
            </a>
            <a
              href="/#inscripciones"
              onClick={closeMobile}
              className="bg-[#EAB308] text-black px-4 py-3 text-xs tracking-[0.18em] uppercase font-bold text-center"
            >
              Inscribir Equipo →
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
